import { type FormEvent, useState } from "react";

import { trpc } from "../trpc";

import { Band, Lotus } from "./Ornaments";
import { SanskritTerm } from "./SanskritTerm";
import { ScrollReveal } from "./ScrollReveal";
import { SectionHead } from "./SectionHead";

const inputClass = "field-line";

/**
 * The Satsangh invitation — lives as chapter 06 on the home page and,
 * with `standalone`, as its own /contact page (page header instead of
 * the verse-numbered section head, extra clearance for the fixed nav).
 */
export function Contact({ standalone = false }: { standalone?: boolean }) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const submitContact = trpc.submitContact.useMutation();
  const submitted = submitContact.isSuccess;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedContact = contact.trim();
    if (!trimmedName || !trimmedContact) {
      return;
    }
    submitContact.mutate({
      name: trimmedName,
      contact: trimmedContact,
      message: message.trim() || undefined,
    });
  }

  const isSending = submitContact.isPending;
  const sendError = submitContact.error;

  const Heading = standalone ? "h1" : "h2";

  return (
    <section
      className={`relative overflow-hidden bg-cream px-7 pb-24 md:pb-32 ${
        standalone ? "pt-36 md:pt-44" : "pt-24 md:pt-32"
      }`}
    >
      <div className="mx-auto max-w-6xl">
        {standalone ? (
          <ScrollReveal>
            <p className="eyebrow">Sit with us</p>
          </ScrollReveal>
        ) : (
          <SectionHead index="06" label="Sit With Us" />
        )}

        <div className="mt-14 grid items-start gap-12 md:mt-20 md:grid-cols-12 md:gap-14">
          <div className="md:col-span-7">
            <ScrollReveal>
              <Heading
                className="font-display leading-[1.15] text-warm-brown"
                style={{
                  fontSize: "clamp(2.4rem, 4.2vw, 3.8rem)",
                  letterSpacing: "0.004em",
                }}
              >
                Come meditate with us.
              </Heading>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <p className="mt-8 max-w-xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
                In Aotearoa, we gather for{" "}
                <SanskritTerm
                  term="Satsangh"
                  devanagari="सत्संग"
                  pronunciation="sut-sung"
                  meaning="Gathering in truth — a spiritual discourse exploring how meditation transforms daily life."
                  lore="'Sat' means truth, 'sangh' means gathering. Each Satsangh explores a different dimension of life through the lens of the Swarved and the Master's teachings."
                />{" "}
                — a space to explore how meditation transforms the everyday. One
                week it might be about making decisions with clarity. Another,
                about understanding the mind. Another, about the peace that
                exists beyond anger.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.25}>
              <p className="mt-5 max-w-xl text-base leading-[1.8] text-warm-brown-light md:text-[1.0625rem]">
                No experience needed. No commitment required.{" "}
                <span className="font-medium text-warm-brown">
                  Just curiosity.
                </span>
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.35}>
              <figure className="plate mt-12 max-w-sm">
                <img
                  src="/images/nature/meditation-nature.jpg"
                  alt="A Vihangam Yog New Zealand sitting"
                  loading="lazy"
                  className="aspect-[3/2] w-full object-cover"
                />
                <figcaption className="plate-caption">
                  Aotearoa Satsangh
                </figcaption>
              </figure>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.2} className="md:col-span-5">
            {submitted ? (
              <div className="hairline-t hairline-b py-12">
                <Lotus size={32} className="mb-5 text-gold opacity-90" />
                <p className="text-2xl font-medium text-warm-brown">
                  Thank you, {name}.
                </p>
                <p className="mt-3 leading-relaxed text-warm-brown-light">
                  We&rsquo;ll be in touch with the details of the next Satsangh.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-7 md:pt-2">
                <label className="block">
                  <span className="eyebrow mb-2 block">Your name</span>
                  <input
                    type="text"
                    placeholder="First and last"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isSending}
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow mb-2 block">Email</span>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    required
                    disabled={isSending}
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow mb-2 block">
                    Anything you&rsquo;d like us to know?
                  </span>
                  <textarea
                    placeholder="Optional"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    disabled={isSending}
                    className={`${inputClass} resize-none`}
                  />
                </label>
                <button
                  type="submit"
                  disabled={isSending}
                  className="w-full rounded-full bg-warm-brown px-5 py-4 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-gold disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSending ? "Sending…" : "I'd like to join"}
                </button>
                {sendError ? (
                  <p role="alert" className="text-sm text-gold">
                    Couldn&rsquo;t send your message — try again in a moment.
                  </p>
                ) : null}
                <p className="pt-2 text-sm leading-relaxed text-warm-gray">
                  Aotearoa Satsangh and online sessions are open to anyone.
                </p>
              </form>
            )}
          </ScrollReveal>
        </div>

        <Band className="mt-24 md:mt-28" />
      </div>
    </section>
  );
}
